import { isCPF, isCNPJ } from 'validation-br'

/**
 * Utilitários de CPF/CNPJ.
 *
 * A partir de jul/2026 o CNPJ passa a ser alfanumérico: os 12 primeiros
 * caracteres podem ser letras (A-Z) ou dígitos e os 2 últimos seguem como
 * dígitos verificadores numéricos. CNPJs numéricos existentes continuam válidos.
 *
 * Forma canônica: maiúsculo, apenas [0-9A-Z], sem pontuação.
 */
export const normalizeDocument = (value?: string | null): string =>
  (value ?? '')
    .toString()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')

/** :CPF | :CNPJ | null — por validação real, nunca por comprimento. */
export const documentType = (value?: string | null): 'CPF' | 'CNPJ' | null => {
  const doc = normalizeDocument(value)
  if (isCPF(doc)) return 'CPF'
  if (isCNPJ(doc)) return 'CNPJ'
  return null
}

/**
 * Documento vindo do cookie `nextauth.document`. Empresa sem CPF/CNPJ no
 * cadastro fazia o login gravar `null`, que o navegador guarda como a string
 * "null" e os formulários pré-preenchiam como documento (SC-11608).
 */
export const sessionDocument = (value?: string | null): string =>
  !value || value === 'null' || value === 'undefined' ? '' : value

export const isValidDocument = (value?: string | null): boolean => {
  const doc = normalizeDocument(value)
  return isCPF(doc) || isCNPJ(doc)
}

const formatCpf = (doc: string): string =>
  doc
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')

// CNPJ: 12 primeiros [0-9A-Z], 2 últimos dígitos -> XX.XXX.XXX/XXXX-XX
const formatCnpj = (doc: string): string => {
  const d = doc.slice(0, 14)
  if (d.length > 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(
      8,
      12
    )}-${d.slice(12)}`
  if (d.length > 8)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`
  return d
}

/**
 * Formata CPF/CNPJ preservando letras. Decide pelo tamanho da forma canônica
 * (<= 11 => CPF; caso contrário CNPJ).
 */
export const formatDocument = (value?: string | null): string => {
  const doc = normalizeDocument(value)
  if (!doc) return ''
  return doc.length <= 11 ? formatCpf(doc) : formatCnpj(doc)
}
